<template>
    <div ref="OverviewRoot" class="overview-root">
        <!-- HEADER -->
        <slot name="header">
            <overview-header :overviewhash="overviewhash" :actions="headerActions" />
        </slot>

        <!-- BODY -->
        <div :class="bodyClass">
            <!-- BACKLINK -->
            <div v-if="!showNewVersion" class="flex-level py-2">
                <slot name="backlink">
                    <CompassBackForwardButton
                        :label="$t.__('common.actions.back')"
                        :link="'javascript:history.back();'"
                        back
                    />
                    <overview-nav
                        :overviewhash="overviewhash"
                        :identity="identity"
                        :splitter="splitter"
                        :use-s-p-a-routing="true"
                        class="flex-right"
                    />
                </slot>
            </div>

            <input :id="`${object.idName}`" type="hidden" :value="dataoverview.linkedid" />
            <input id="onOverview" type="hidden" value="Y" />

            <!-- CONTENT -->
            <slot name="content">
                <div :class="wrapperClass">
                    <div v-if="showNewVersion" class="overview__navbar">
                        <CompassSideNav :menuItems="formattedNavItems" />
                    </div>
                    <div v-else class="overview-navbar">
                        <ul class="well mt-0 mb-3">
                            <li v-for="(item, index) in informationsNav" :key="index">
                                <router-link
                                    :to="routerLinkParams(item)"
                                    :data-bot="item.databot"
                                    active-class="primary-50"
                                    class="flex-level jc-space gray-100 fs-3 px-2 py-2 pointer hover-primary-05-bkg semibold"
                                >
                                    {{ item.label }}
                                    <i class="icon-arrow-back dinlineb rotated-180 gray-60"></i>
                                </router-link>
                            </li>
                        </ul>

                        <!-- NOTE -->
                        <el-collapse
                            v-model="activeNoteCollapse"
                            accordion
                            class="well sidemenu-overview sidemenu-overview-note__container"
                            :class="activeNoteClass"
                        >
                            <el-collapse-item name="sidemenu-overview-note">
                                <template #title>
                                    <span>
                                        {{ $l10n.__('Note') }}
                                    </span>
                                </template>
                                <div
                                    v-if="permissionsToEdit"
                                    v-click-outside.stop="submitNote"
                                    @click="setActiveNoteClass(true)"
                                >
                                    <div>
                                        <RichText
                                            ref="refNotesRichText"
                                            v-model="note"
                                            :placeholder="$t.__('common.actions.write_note')"
                                            :options="optionsRichText"
                                            :is-custom="true"
                                            :show-rich-text-menu="false"
                                            class="notes-richtext"
                                        />
                                        <RichTextMenu
                                            v-if="richTextEditor"
                                            :editor-menu="richTextEditor"
                                            :options="optionsRichText"
                                            class="notes-richtext-menu"
                                        />
                                    </div>

                                    <CompassButton
                                        v-if="canImportOldNote"
                                        :label="$t.__('overview.Overview.see_old_datas')"
                                        class="mt-1"
                                        type="tertiary"
                                        size="small"
                                        left-icon="attachment"
                                        @click="openDialog('import-old-note')"
                                    />
                                </div>

                                <div
                                    v-else
                                    class="sidemenu-overview-note__content pr-3 my-2"
                                    v-html="note"
                                />
                            </el-collapse-item>
                        </el-collapse>

                        <div
                            v-show="noteIsEdited"
                            class="sidemenu-overview-note__buttons flex-row mt-3"
                        >
                            <CompassButton
                                :label="$t.__('common.actions.cancel')"
                                type="secondary"
                                @click="cancelEditNote"
                            />
                            <CompassButton
                                :label="$t.__('common.actions.save')"
                                class="ml-3"
                                @click.stop="submitNote"
                            />
                        </div>

                        <!-- MODULES -->
                        <template v-if="pluginsNav.length">
                            <h2>{{ $l10n.__('Modules') }}</h2>
                            <el-menu class="well mt-0 mb-3 sidemenu-overview" router>
                                <template v-for="(item, index) in pluginsNav">
                                    <el-submenu v-if="item.subNav" :key="index" :index="item.id">
                                        <template #title>
                                            <span class="semibold p-2 block">{{ item.label }}</span>
                                        </template>
                                        <el-menu-item
                                            v-for="(subnav, subindex) in item.subNav"
                                            :key="subindex"
                                        >
                                            <router-link
                                                :to="routerLinkParams(subnav)"
                                                :data-bot="subnav.databot"
                                                active-class="primary-50"
                                                class="flex-level jc-space gray-100 fs-3 pr-2 py-2 pointer hover-primary-05-bkg semibold"
                                            >
                                                {{ subnav.label }}
                                                <i
                                                    class="icon-arrow-back dinlineb rotated-180 gray-60"
                                                ></i>
                                            </router-link>
                                        </el-menu-item>
                                    </el-submenu>
                                    <el-menu-item v-else :key="index" class="p-0">
                                        <router-link
                                            :key="index"
                                            :to="routerLinkParams(item)"
                                            :data-bot="item.databot"
                                            active-class="primary-50"
                                            class="flex-level jc-space gray-100 fs-3 px-2 py-2 pointer hover-primary-05-bkg semibold"
                                        >
                                            {{ item.label }}
                                            <i
                                                class="icon-arrow-back dinlineb rotated-180 gray-60"
                                            ></i>
                                        </router-link>
                                    </el-menu-item>
                                </template>
                            </el-menu>
                        </template>

                        <div v-if="moreNav && moreNav.length > 0">
                            <div
                                v-for="(component, componentIndex) in moreNav"
                                :key="componentIndex"
                                class="well mb-3 mt-0"
                            >
                                <component
                                    :is="component.component"
                                    v-if="component.component"
                                    v-bind="component.data"
                                />
                            </div>
                        </div>
                    </div>
                    <div v-if="showNewVersion" class="overview__container">
                        <router-view
                            :key="$route.path"
                            :overviewhash="overviewhash"
                            :more="currentNavButtons"
                        ></router-view>
                        <div class="overview__sidebar" :class="sidebarClass">
                            <div class="overview__sidebar-toggle-wrapper">
                                <CompassTooltip :arrow="false">
                                    <button
                                        class="overview__sidebar-toggle"
                                        :class="toggleButtonClass"
                                        @click="toggleSidebar"
                                    >
                                        <CompassIcon icon="caret-right" size="small" />
                                    </button>
                                    <template #content>
                                        <span>{{ tooltipLabelToggleButton }}</span>
                                        <span
                                            class="tooltip__shortcut typographies-paragraph-x-small-regular"
                                        >
                                            {{ shortcut }}
                                            <CompassIcon :icon="shortcutIcon" size="extra-small" />
                                        </span>
                                    </template>
                                </CompassTooltip>
                            </div>
                            <div class="overview__sidebar-wrapper">
                                <OverviewNote
                                    :permissionsToEdit="permissionsToEdit"
                                    :canImportOldNote="canImportOldNote"
                                    :linkedid="dataoverview.linkedid"
                                    :overviewType="currentOverviewType"
                                    :linkedtype="dataoverview.linkedtype"
                                />
                                <OverviewContactBlock
                                    v-if="dataoverview.linkedtype !== 'people'"
                                    :linkedid="dataoverview.linkedid"
                                    :linkedtype="dataoverview.linkedtype"
                                />
                                <OverviewCompanyBlock
                                    v-if="dataoverview.linkedtype !== 'people'"
                                    :linkedid="dataoverview.linkedid"
                                    :linkedtype="dataoverview.linkedtype"
                                />
                                <template v-if="moreNav && moreNav.length > 0">
                                    <DrawerAccordion
                                        v-for="(component, componentIndex) in moreNav"
                                        :id="component.id"
                                        :key="componentIndex"
                                        :title="SellsyfyTitle(component.label)"
                                    >
                                        <component
                                            :is="component.component"
                                            v-if="component.component"
                                            v-bind="component.data"
                                        />
                                    </DrawerAccordion>
                                </template>
                            </div>
                        </div>
                    </div>
                    <router-view
                        v-else
                        :key="$route.path"
                        :overviewhash="overviewhash"
                        :more="currentNavButtons"
                    ></router-view>
                </div>
            </slot>
        </div>

        <!-- SIDE PANEL -->
        <OverviewPanel
            v-if="showPanel"
            :value="panel"
            :loading="panelLoading"
            @loaded="loadedPanel"
            @close="closePanel"
        ></OverviewPanel>

        <!-- DIALOG -->
        <OverviewDialog v-if="showDialog" :data="dialog"> </OverviewDialog>
    </div>
</template>

<script>
import OverviewNav from './OverviewNav.vue';
import OverviewHeader from './OverviewHeader.vue';

import CorporationDataV2 from 'vcomponents/sellsyadmin/CorporationData_v2.vue';
import CorporationDataV3 from 'vcomponents/sellsyadmin/CorporationData_v3.vue';
import Connection from 'vcomponents/sellsyadmin/Connection.vue';

import OverviewDialog from 'vcomponents/popups/overviews/OverviewDialog.vue';
import OverviewPanel from './panels/OverviewPanel.vue';

import RichText from 'vcomponents/richtext/RichText.vue';
import RichTextMenu from 'vcomponents/richtext/RichTextMenu.vue';
import Feature from 'Services/Feature';

import OverviewContactBlock from 'vcomponents/overview/OverviewContactBlock.vue';
import OverviewCompanyBlock from 'vcomponents/overview/OverviewCompanyBlock.vue';
import OverviewNote from 'vcomponents/overview/OverviewNote.vue';

import DrawerAccordion from 'vcomponents/common/drawer/accordion/DrawerAccordion.vue';
import LocalStorage from 'Core/commons/LocalStorage';
import { isMacOS } from 'Helpers/userAgentHelper';
import { normalizeKeyName } from 'Helpers/normalizeKeyHelper';

export default {
    components: {
        'overview-nav': OverviewNav,
        'overview-header': OverviewHeader,
        CorporationDataV2,
        CorporationDataV3,
        Connection,
        OverviewDialog,
        OverviewPanel,
        RichText,
        RichTextMenu,
        OverviewContactBlock,
        OverviewCompanyBlock,
        DrawerAccordion,
        OverviewNote,
    },
    props: {
        name: {
            type: String,
            required: true,
            default: '',
        },
        dataoverview: {
            required: false,
            type: Object,
            default: () => {},
        },
        overviewClass: {
            required: true,
            type: Object,
        },
        nav: {
            type: Array,
            required: true,
        },
        moreNav: {
            type: Array,
            required: false,
            default: () => [],
        },
        contextId: {
            type: [String, Boolean],
            required: false,
            default: false,
        },
    },
    data() {
        return {
            overviewhash: '',
            defaultTab:
                localStorage.getItem(`defaultTab:${this.overviewClass?.linkedtype}`) ?? null,
            headerActions: [],
            object: false,
            splitter: /\/(\d+)/,
            note: '',
            loadingNote: false,
            previousNote: '',
            activeNote: false,
            activeNoteCollapse: '',
            oldNote: '',
            panel: {
                data: {},
                settings: {},
            },
            dialog: {},
            showPanel: false,
            showDialog: false,
            panelLoading: true,
            optionsRichText: [
                'Heading',
                'Bold',
                'Italic',
                'Underline',
                'BulletList',
                'OrderedList',
                'TextAlign',
                'Divider',
                'Link',
            ],
            richTextEditor: null,
            displaySidebar: true,
            localstorage: new LocalStorage('sidebar', 'overview'),
        };
    },
    computed: {
        bodyClass() {
            return [
                this.showNewVersion
                    ? 'overview__body'
                    : 'flex-column ov-reset flex self-center wp-10',
                this.showNewVersion && this.displaySidebar ? 'overview__body--has-sidebar' : '',
            ];
        },
        wrapperClass() {
            return this.showNewVersion ? 'overview__wrapper' : 'flex-row';
        },
        sidebarClass() {
            return this.displaySidebar
                ? 'overview__sidebar--is-open'
                : 'overview__sidebar--is-closed';
        },
        toggleButtonClass() {
            return this.displaySidebar
                ? 'overview__sidebar-toggle--close'
                : 'overview__sidebar-toggle--open';
        },
        formattedNavItems() {
            const informationNav = this.informationsNav.map((item) => {
                return {
                    ...item,
                    route: this.routerLinkParams(item),
                    active: this.$route.name === this.routerLinkParams(item)?.name,
                };
            });

            const modulesNav = this.pluginsNav.map((item) => {
                return {
                    ...item,
                    route: this.routerLinkParams(item),
                    active: this.$route.name === this.routerLinkParams(item)?.name,
                    subItems:
                        item.subNav?.map((subItem) => {
                            return {
                                ...subItem,
                                route: this.routerLinkParams(subItem),
                                active: this.$route.name === this.routerLinkParams(subItem)?.name,
                            };
                        }, []) ?? undefined,
                };
            });
            return [...informationNav, ...modulesNav];
        },
        informationsNav() {
            return this.nav.find((nav) => nav.id === 'informations').items;
        },
        pluginsNav() {
            return this.nav.find((nav) => nav.id === 'plugin').items;
        },
        currentNavButtons() {
            return (
                this.overviewClass
                    .getNav()
                    .map((section) => section.items)
                    .flat()
                    .find((item) => item.id === _.last(this.$route.name.split('.')))?.more || {}
            );
        },
        identity() {
            return this.dataoverview.data?.identity;
        },
        permissionsToEdit() {
            return this.dataoverview.permissions.can_edit;
        },
        activeNoteClass() {
            return this.activeNote ? 'b-1-primary-50' : '';
        },
        noteIsEdited() {
            return this.activeNote && this.note !== this.previousNote;
        },
        canImportOldNote() {
            return this.isOpportunity && this.oldNote?.length > 0;
        },
        isThird() {
            if (['client', 'prospect', 'supplier'].includes(this.identity.type)) {
                return !this.dataoverview.data.identity.isCorporation ? 'individuals' : 'companies';
            }
            return false;
        },
        isOpportunity() {
            return this.overviewClass?.linkedtype === 'opportunity';
        },
        currentOverviewType() {
            let overviewType = this.isThird ? this.isThird : this.overviewClass.linkedtype;

            switch (overviewType) {
                case 'people':
                    overviewType = 'contacts';
                    break;
                case 'opportunity':
                    overviewType = 'opportunities';
                    break;
                default:
                    break;
            }
            return overviewType;
        },
        showNewVersion() {
            return Feature.isEnabled('overview-info-sidebar');
        },
        tooltipLabelToggleButton() {
            return this.displaySidebar
                ? this.$t.__('common.actions.reduce')
                : this.$t.__('common.actions.expand');
        },
        shortcut() {
            return `${normalizeKeyName('Ctrl')} +`;
        },
        shortcutIcon() {
            return this.displaySidebar ? 'caret-right-fill' : 'caret-left-fill';
        },
    },
    mounted() {
        this.displaySidebar = this.localstorage.get() ?? true;
        // Get the editor instance from RichText component
        this.richTextEditor = this.$refs.refNotesRichText?.editor;

        if (Feature.isEnabled('overview-info-sidebar')) {
            window.addEventListener('keydown', this.sidebarKeyboardEvents);
        }
    },
    created() {
        this.headerActions = this.overviewClass.getHeaderActions();
        this.object = this.overviewClass.getObject();
        document.title = this.dataoverview.data?.identity?.name;

        this.getNote();
        if (this.isOpportunity) {
            this.getOldNote();
        }

        const getUrlParams = new URLSearchParams(window.location.search);
        const getParamTimelineFiltersTabs = getUrlParams.get('redirectTab');
        /**
         * If current route go on overview base, go to default tab
         */
        if (
            [`${this.name}.overview.activity`, `${this.name}.overview`].includes(
                this.$route.name,
            ) &&
            !getParamTimelineFiltersTabs
        ) {
            const routeName =
                this.defaultTab &&
                this.$router.resolve({ name: `${this.name}.overview.${this.defaultTab}` }).href !==
                    '/'
                    ? `${this.name}.overview.${this.defaultTab}`
                    : null;

            if (!routeName || this.defaultTab === 'activity' || this.$route.query.comment) return;
            this.$router.replace({ name: routeName, query: { contextId: this.contextId } });
        }

        //check if allowed tab based on permissions
        const permission = `can_show_${this.defaultTab}`;
        if (
            this.dataoverview.permissions.hasOwnProperty(permission) &&
            !this.dataoverview.permissions[permission]
        ) {
            this.$router.push({
                name: `${this.name}.overview.activity`,
                query: { contextId: this.contextId },
            });
        }
        EventBus.on('overview:closeDialog', this.closeDialog);
        EventBus.on('overview:importOldNote', this.importOldNote);
        EventBus.on('overview:closePanel', this.closePanel);
        EventBus.on('overview:openPanel', this.openPanel);
    },

    beforeDestroy() {
        this.$storeHasModule('pricing') ? this.$store.unregisterModule('pricing') : null;
        this.$storeHasModule('cart') ? this.$store.unregisterModule('cart') : null;
        this.$storeHasModule('customer') ? this.$store.unregisterModule('customer') : null;
        this.$storeHasModule('catalog') ? this.$store.unregisterModule('catalog') : null;
        this.$storeHasModule('global') ? this.$store.unregisterModule('global') : null;

        if (Feature.isEnabled('overview-info-sidebar')) {
            window.removeEventListener('keydown', this.sidebarKeyboardEvents);
        }

        EventBus.off('overview:closeDialog', this.closeDialog);
        EventBus.off('overview:importOldNote', this.importOldNote);
        EventBus.off('overview:closePanel', this.closePanel);
        EventBus.off('overview:openPanel', this.openPanel);
    },
    methods: {
        sidebarKeyboardEvents(event) {
            if (
                event.shiftKey ||
                event.target.classList.contains('tiptap') ||
                event.target.closest('.tiptap')
            ) {
                return;
            }

            if (
                !this.displaySidebar &&
                event.key === 'ArrowLeft' &&
                (event.ctrlKey || (isMacOS() && event.metaKey))
            ) {
                event.preventDefault();
                this.displaySidebar = true;
                this.localstorage.set(this.displaySidebar);
            }
            if (
                this.displaySidebar &&
                event.key === 'ArrowRight' &&
                (event.ctrlKey || (isMacOS() && event.metaKey))
            ) {
                event.preventDefault();
                this.displaySidebar = false;
                this.localstorage.set(this.displaySidebar);
            }
        },
        SellsyfyTitle(title) {
            return title === 'Connexion' ? 'Sellsy Connexion' : 'Sellsy Admin';
        },
        closeDialog() {
            this.showDialog = false;
        },
        openDialog(dialog) {
            const dialogName = dialog;
            this.dialog = { name: dialogName, params: { 'old-note-value': this.oldNote } };
            this.showDialog = dialogName ?? false;
        },
        loadedPanel() {
            this.panelLoading = false;
        },
        closePanel() {
            this.panelLoading = true;
            this.showPanel = false;
        },
        openPanel(data) {
            if (data && data?.id) {
                const panelData = data;

                this.panelLoading = panelData.id !== this.panel.id;
                this.showPanel = true;
                this.panel = panelData;
            } else {
                this.closePanel();
                this.$pushToast({
                    type: 'error',
                    icon: true,
                    text: this.$l10n.__(
                        "Une erreur est survenue lors du chargement de l'élement. Veuillez réessayer.",
                    ),
                    close: true,
                    unique: true,
                });
            }
        },
        routerLinkParams(item) {
            const toMeta = {
                name: `${this.name}.overview.${item.id}`,
                params: { type: this.overviewClass.linkedtype, id: this.overviewClass.linkedid },
            };
            return this.contextId
                ? { ...toMeta, ...{ query: { contextId: this.contextId } } }
                : toMeta;
        },
        setActiveNoteClass(active) {
            this.activeNote = active;
        },
        isNote(note) {
            return ['description', 'stickyNote', 'brief'].includes(note);
        },
        getOldNote() {
            this.oldNote =
                this.dataoverview?.data?.informations?.find((item) => item.key === 'stickyNote')
                    ?.value || '';
        },
        getNote() {
            const storedNote = this.dataoverview?.data?.informations?.find((item) =>
                this.isNote(item.key),
            );

            if (storedNote?.value) {
                this.activeNoteCollapse =
                    storedNote?.value !== '<p></p>' ? 'sidemenu-overview-note' : '';

                this.note = storedNote.value;
                this.previousNote = storedNote.value;
            }
        },
        cancelEditNote() {
            this.note = this.previousNote;
            this.setActiveNoteClass(false);
        },

        submitNote() {
            this.setActiveNoteClass(false);

            if (this.previousNote !== this.note) {
                ApiClient.patch(
                    `/${this.currentOverviewType}/${this.overviewClass.linkedid}/note`,
                    {},
                    { note: this.note },
                )
                    .then((res) => {
                        this.previousNote = this.note;
                    })
                    .catch((data) => {
                        if (data?.response?.status === 403) {
                            Push.error(
                                this.$l10n.__("Vous n'avez pas la permission d'éditer cette note"),
                            );
                            return;
                        }

                        Push.error(this.$l10n.__('Une erreur est survenue'));
                    });
            }
        },
        //save current note as a merge version of note v1 + note v2
        importOldNote() {
            this.loadingNote = true;
            const mergedNotes =
                this.note.length > 0 && this.note !== '<p></p>'
                    ? `${this.note}<p></p><p></p>${this.oldNote}`
                    : this.oldNote;

            this.$ajax.post(
                `/rest/prospection/opportunities/${this.overviewClass.linkedid}/migrateStickyNote`,
                {},
                { note: mergedNotes },
                () => {
                    this.note = mergedNotes;
                    this.loadingNote = false;
                    this.previousNote = this.note;
                    this.closeDialog();
                },
                () => {
                    Push.error(this.$l10n.__('Une erreur est survenue'));
                    this.loadingNote = false;
                    this.note = this.previousNote;
                    this.closeDialog();
                },
            );
        },
        toggleSidebar() {
            this.displaySidebar = !this.displaySidebar;
            this.localstorage.set(this.displaySidebar);
        },
    },
};
</script>

<style lang="scss" scoped>
.overview-root {
    display: flex;
    flex-direction: column;
    height: 100%;
}
</style>

<style lang="scss">
@import '~@sellsy/styleguide/src/_variables';

.overview {
    &__body {
        flex: 1;
        margin: -32px -24px -32px 0;
        min-height: 0;
        overflow-x: hidden;
        padding-right: 18px;
        position: relative;
        transition: all 200ms ease-in-out;

        &--has-sidebar {
            padding-right: 314px;
        }
    }

    &__wrapper {
        align-items: stretch;
        display: flex;
        flex-direction: row;
        height: 100%;
        overflow-y: auto;
        padding: 0 var(--spacing-4) 0 0;
    }

    &__navbar {
        flex-shrink: 0;
        margin: var(--spacing-4) var(--spacing-4) 0 0;
        width: 216px;

        &:after {
            content: '';
            display: block;
            height: var(--spacing-5);
        }

        .compass-side-nav__item {
            &[data-bot='client-overview__menu--summary'] {
                .compass-icon {
                    path {
                        fill: var(--colors-sellsy-ai-50);
                    }
                }
            }

            &-icon {
                align-items: center;
                background: var(--colors-sellsy-ai-05);
                border-radius: 50%;
                display: inline-flex;
                height: 20px;
                justify-content: center;
                width: 20px;
            }
        }
    }

    &__container {
        flex: 1;
        min-width: 0;

        &:after {
            content: '';
            display: block;
            height: var(--spacing-5);
        }
    }

    &__sidebar {
        background: var(--colors-common-background-default);
        border-left: 2px solid var(--colors-common-border-lighter);
        bottom: 0;
        position: absolute;
        right: 0;
        top: 0;
        transition: transform 200ms ease-in-out;
        width: 314px;

        &--is-closed {
            transform: translateX(calc(100% - 18px));

            .overview__sidebar-wrapper {
                opacity: 0;
                transition: opacity 0ms ease-in-out 200ms;
            }
        }

        &--is-open {
            transform: translateX(0);
        }

        &-wrapper {
            height: 100%;
            overflow-x: hidden;
            overflow-y: auto;
            transition: opacity 0ms ease-in-out;
        }

        &-toggle {
            align-items: center;
            background: var(--colors-common-background-default);
            border: 1px solid var(--colors-common-border-lighter);
            border-radius: 50%;
            cursor: pointer;
            display: flex;
            height: var(--spacing-4);
            justify-content: center;
            padding: 0;
            transition: all 100ms ease-in-out;
            width: var(--spacing-4);

            &:hover {
                background: var(--colors-common-background-hover);
            }

            &-wrapper {
                left: 0;
                position: absolute;
                top: 12px;
                transform: translateX(-50%);
                z-index: 10;
            }

            .compass-icon {
                height: var(--spacing-2);
                transform: rotate(0);
                transition: all 200ms ease-in-out;
                width: var(--spacing-2);
            }

            &--open {
                .compass-icon {
                    transform: rotate(180deg);
                }
            }
        }

        &-wrapper {
            .overview-contact-block {
                & + .overview-company-block {
                    margin: var(--spacing-3) 0 0;
                }
            }
        }
    }
}

.tooltip__shortcut {
    align-items: center;
    background: var(--colors-common-background-darker-hover, #565665);
    border-radius: var(--xx-small, 4px);
    display: inline-flex;
    gap: var(--spacing-0);
    padding: var(--spacing-0);
}

.page-thirds-overview {
    #sellsy_app {
        &:has(.overview__container) {
            overflow: hidden;
        }
    }
}

.overview-navbar {
    margin-right: var(--spacing-4);
    width: 320px;
}

.sidemenu-overview {
    .el-submenu .el-menu-item,
    .el-menu-item,
    .el-submenu__title {
        line-height: normal;
        height: auto;
    }

    .el-submenu__title {
        padding-left: 0 !important;
        padding-right: 8px;

        .el-submenu__icon-arrow {
            right: 8px;
            font-weight: bold;
            font-size: 14px;
            color: $gray-60;
        }
    }

    .el-submenu {
        .el-menu-item {
            padding: 0 !important;
            a {
                padding-left: 20px;
            }
        }
    }

    // NOTE
    &.sidemenu-overview-note__container {
        .el-collapse-item {
            .sidemenu-overview-note__content {
                margin: 0;
                overflow: auto;
                height: 208px;
                p {
                    display: list-item;
                    margin: 0;
                    line-height: 20px;
                    font-weight: 400;
                }
            }
            &.is-active {
                position: relative;
            }
            div.el-collapse-item__header {
                padding: 0;
                border: unset;
                display: flex;
                span {
                    font-weight: 700;
                }
                &,
                .el-collapse-item__arrow {
                    height: 20px;
                    line-height: 20px;
                }
            }
            .el-collapse-item__wrap {
                &,
                .editor-input,
                .editor-input .el-button,
                .editor-input .ProseMirror,
                .menu-bar-editor {
                    border: unset;
                }
                .editor-input {
                    .ProseMirror {
                        height: 180px;
                        overflow: auto;
                        padding-left: 0;
                        padding-top: 0;
                        padding-bottom: 0;
                        margin-bottom: 8px;
                        margin-top: 8px;

                        p::before {
                            line-height: 20px;
                            font-weight: 400;
                            font-style: normal;
                        }
                    }
                }
            }
            ::-webkit-scrollbar {
                width: 5px;
            }
            ::-webkit-scrollbar-thumb {
                background: $gray-05;
                border-radius: 10px;
            }
        }
    }
}
.sidemenu-overview-note__buttons {
    justify-content: end;
    display: flex;
}

.notes-richtext {
    .ProseMirror {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        width: 100%;
        height: 180px;
        max-height: 180px;
        padding: 0;
        overflow: auto;

        color: var(--colors-common-text-standard-default);
        font-family: Proxima Nova;
        font-size: var(--font-size-14);
        font-style: normal;
        font-weight: var(--font-weights-proxima-nova-1);
        line-height: var(--line-heights-20);

        ul,
        ol {
            padding: 0 2rem;
        }

        p,
        h1,
        h2,
        h3,
        h4,
        h5,
        h6 {
            width: 100%;
            margin: 0;
        }

        &:focus-visible {
            outline: none;
        }
    }

    &-menu {
        .menu {
            display: flex;
            gap: var(--spacing-0);
        }
    }
}
</style>
